// src/components/CardResumenReservaPay.jsx
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RoomIcon from "@mui/icons-material/Room";

const CardResumenReservaPay = ({
  roomName,
  startDate,
  endDate,
  pricePerNight,
  total,
  numGuests,
  seña,
  commission,
  amountToPay,
  currency = "ARS", // 💱 "ARS" o "CLP"
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const formatUTCDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };

  const nights =
    startDate && endDate
      ? Math.max(
          (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24),
          1
        )
      : 1;

  const formattedRoomName =
    roomName?.charAt(0).toUpperCase() + roomName?.slice(1);

  const safeTotal = Number(total) || 0;
  const safeSeña = Number(seña) || 0;
  const safeAmountToPay = Number(amountToPay) || 0;

  // (estos cálculos los dejo por si los usás en otro lado más adelante)
  const subtotal = (Number(pricePerNight) || 0) * nights * (numGuests || 1);
  const debePagar = Math.max(safeTotal - safeSeña, 0);
  const comisionExtra =
    commission === 0 && safeSeña === 0 ? Math.round(debePagar * 0.05) : 0;
  const totalConComision = debePagar + comisionExtra;

  useEffect(() => {
    console.log("💰 Nuevo monto a pagar:", safeAmountToPay);
  }, [safeAmountToPay]);

  // 💱 locale solo para el formato de miles/decimales
  const locale = currency === "CLP" ? "es-CL" : "es-AR";

  const formatMoneyNumber = (value) => {
    const num = Number(value) || 0;
    return new Intl.NumberFormat(locale, {
      style: "decimal",
      minimumFractionDigits: 0,
    }).format(num);
  };

  const renderMoney = (value) => `$ ${formatMoneyNumber(value)} ${currency}`;

  return (
    <Box
      sx={{
        position: { sm: "static", md: "sticky" },
        top: 100,
        backgroundColor: "#fff",
        padding: 3,
        borderRadius: 4,
        boxShadow: "0px 2px 15px rgba(0,0,0,0.1)",
        marginX: "auto",
        marginBottom: 4,
      }}
    >
      {/* Encabezado */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
        <RoomIcon sx={{ fontSize: 40 }} />
        <Box>
          <Typography fontWeight="bold" fontSize="1.2rem">
            Confirmación de reserva
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ⭐ 5.0 (12) · Favorito entre huéspedes
          </Typography>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Cancelación gratuita</strong>
        <br />
        <a href="#" style={{ color: "#1976D2", fontWeight: "bold" }}>
          Ver política completa
        </a>
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Fechas y huéspedes */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography fontWeight="bold">
            {startDate && endDate
              ? `${formatUTCDate(startDate)} – ${formatUTCDate(endDate)}`
              : "Fechas no definidas"}
          </Typography>
          <Typography variant="body2">
            {numGuests} huésped{numGuests === 1 ? "" : "es"} · {nights} noche
            {nights === 1 ? "" : "s"}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Detalle de costos */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography>Total de la reserva</Typography>
        <Typography>{renderMoney(safeTotal)}</Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography>Ya pagado</Typography>
        <Typography>– {renderMoney(safeSeña)}</Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography fontWeight="bold">Total a pagar</Typography>
        <Typography fontWeight="bold">
          {renderMoney(safeAmountToPay)}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        sx={{ mt: 1, color: "#1976D2", cursor: "pointer" }}
      >
        ¿Cómo se calcula el total?
      </Typography>
    </Box>
  );
};

export default CardResumenReservaPay;
